import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { fetchRooms, createRoom, joinRoom, deleteRoom } from '../api_fs';
import { Play, Plus, Lock, ArrowLeft, RefreshCw, Users, Trash2 } from 'lucide-react';

interface FreestyleLobbyProps {
  user: User;
  onBack: () => void;
}

export const FreestyleLobby: React.FC<FreestyleLobbyProps> = ({ user, onBack }) => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomPassword, setNewRoomPassword] = useState('');
  
  const [joinRoomId, setJoinRoomId] = useState<string | null>(null);
  const [joinPassword, setJoinPassword] = useState('');

  const loadRooms = async () => {
    try {
      setLoading(true);
      const data = await fetchRooms();
      setRooms(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
    const interval = setInterval(loadRooms, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCreate = async () => {
    if (!newRoomName) return;
    try {
      const data = await createRoom(newRoomName, newRoomPassword);
      navigate(`/freestyle/room/${data.room.id}`);
    } catch (e: any) {
      alert(e.message || 'Error creating room');
    }
  };

  const handleJoin = async (id: string, hasPassword: boolean) => {
    if (hasPassword && joinRoomId !== id) {
      setJoinRoomId(id);
      return;
    }
    try {
      const data = await joinRoom(id, joinPassword);
      navigate(`/freestyle/room/${data.room.id}`);
    } catch (e: any) {
      alert(e.message || 'Error joining room');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this room? / 确定要删除这个房间吗？")) return;
    try {
      await deleteRoom(id);
      loadRooms();
    } catch (e: any) {
      alert(e.message || 'Error deleting room');
    }
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-start relative overflow-y-auto overflow-x-hidden p-4 md:p-8">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/50 text-white/70 backdrop-blur-sm active:scale-95 md:hidden"
        aria-label="返回"
      >
        <ArrowLeft size={18} />
      </button>
      <h1 className="text-3xl md:text-5xl font-orbitron font-black text-purple-500 mb-8 tracking-widest text-center drop-shadow-[0_0_10px_rgba(168,85,247,0.5)]">
          FREESTYLE LOBBY
          <br/>
          <span className="text-xl md:text-3xl text-purple-500/80">自由式打分大厅</span>
      </h1>

      {showCreate ? (
        <div className="w-full max-w-md bg-zinc-900/80 p-6 rounded-2xl border border-zinc-700 backdrop-blur-sm flex flex-col gap-4">
          <h2 className="text-xl font-orbitron text-white">Create Room / 创建房间</h2>
          <input 
            type="text" 
            placeholder="Room Name / 房间名称" 
            value={newRoomName} 
            onChange={e => setNewRoomName(e.target.value)}
            className="bg-black/50 border border-zinc-700 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
          />
          <input 
            type="password" 
            placeholder="Password (Optional) / 密码（可选）" 
            value={newRoomPassword} 
            onChange={e => setNewRoomPassword(e.target.value)}
            className="bg-black/50 border border-zinc-700 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
          />
          <div className="flex gap-4 mt-4">
            <button onClick={() => setShowCreate(false)} className="flex-1 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700">Cancel / 取消</button>
            <button onClick={handleCreate} className="flex-1 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500 font-bold">Create / 创建</button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-2xl flex flex-col gap-4">
          <div className="flex justify-between items-center mb-4">
            <button onClick={loadRooms} className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh / 刷新
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-purple-600/20 text-purple-400 border border-purple-500/50 px-4 py-2 rounded-lg hover:bg-purple-600/40 hover:text-purple-300 transition-colors">
              <Plus size={18} /> Create Room / 创建房间
            </button>
          </div>

          <div className="flex flex-col gap-3 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
            {rooms.length === 0 && (
              <div className="text-center text-zinc-500 py-8 font-rajdhani">No active rooms found. / 暂无活动房间。</div>
            )}
            {rooms.map(room => (
              <div key={room.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-center gap-4 hover:border-purple-500/30 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  {room.hasPassword && <Lock size={16} className="text-zinc-500" />}
                  <span className="text-xl font-orbitron text-white">{room.name}</span>
                  <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">{room.status}</span>
                </div>
                
                    {joinRoomId === room.id ? (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <input 
                      type="password" 
                      placeholder="Password / 密码" 
                      value={joinPassword} 
                      onChange={e => setJoinPassword(e.target.value)}
                      className="bg-black border border-zinc-700 rounded p-2 text-white text-sm w-full sm:w-32 focus:border-purple-500 outline-none"
                    />
                    <button onClick={() => handleJoin(room.id, true)} className="bg-purple-600 px-4 py-2 rounded text-sm text-white hover:bg-purple-500 whitespace-nowrap">Join / 加入</button>
                    <button onClick={() => setJoinRoomId(null)} className="bg-zinc-800 px-3 py-2 rounded text-sm text-white hover:bg-zinc-700">X</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {(room.ownerId === user.uid || user.isAdmin) && (
                      <button onClick={() => handleDelete(room.id)} className="bg-red-900/30 text-red-400 p-2 rounded-lg hover:bg-red-900/50 transition-colors" title="Delete Room">
                        <Trash2 size={16} />
                      </button>
                    )}
                    <button onClick={() => handleJoin(room.id, room.hasPassword)} className="flex items-center gap-2 bg-zinc-800 text-white px-4 py-2 rounded-lg hover:bg-purple-600 hover:text-white transition-colors w-full sm:w-auto justify-center">
                      <Users size={16} /> Join as Judge / 加入裁判
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
